// services/UserService.js
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore'
import { app } from '../firebase'

class UserService {
  constructor () {
    this.db = getFirestore(app)
    this.companyIdCache = new Map()
  }

  /**
   * Get user's companyId from Firestore
   * Uses cache to minimize reads
   */
  async getUserCompanyId (userId) {
    if (!userId) {
      throw new Error('User ID is required')
    }

    // 1. Check cache first
    if (this.companyIdCache.has(userId)) {
      return this.companyIdCache.get(userId)
    }

    // 2. Return a new Promise that uses onSnapshot
    return new Promise((resolve, reject) => {
      const userDocRef = doc(this.db, 'users', userId)

      // Set a timeout to prevent hanging forever (e.g., 10 seconds)
      const timeoutId = setTimeout(() => {
        unsubscribe() // Stop listening
        reject(
          new Error(
            'User profile or companyId not found in time. Please contact support.'
          )
        )
      }, 10000) // 10-Sekunden-Timeout

      // Listen for changes to the user document
      const unsubscribe = onSnapshot(
        userDocRef,
        userDoc => {
          if (userDoc.exists()) {
            const companyId = userDoc.data().companyId

            // Check if companyId exists
            if (companyId) {
              clearTimeout(timeoutId) // Cancel the timeout
              unsubscribe() // Stop listening

              // Cache for future use
              this.companyIdCache.set(userId, companyId)

              // Resolve the promise with the companyId
              resolve(companyId)
            }
            // If companyId doesn't exist yet, we just keep listening...
            // The Cloud Function might still be writing.
          }
          // If userDoc doesn't exist yet, we also keep listening.
        },
        error => {
          // Handle Firestore errors
          clearTimeout(timeoutId)
          console.error('Error listening to user document:', error)
          reject(error)
        }
      )
    })
  }

  /**
   * Clear cache for a specific user or all users
   */
  clearCache (userId = null) {
    if (userId) {
      this.companyIdCache.delete(userId)
    } else {
      this.companyIdCache.clear()
    }
  }

  /**
   * Get cache size
   */
  getCacheSize () {
    return this.companyIdCache.size
  }
}

// Singleton Export
export default new UserService()
